import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { LayoutDashboard, CalendarCheck, MessageSquare, ImageIcon, Star, LogOut, Trash2, Plus, Upload, Loader2, Settings } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";

const STATUSES = ["New", "In-Progress", "Confirmed", "Closed"];
const statusColor = {
  New: "bg-blue-100 text-blue-700", "In-Progress": "bg-amber-100 text-amber-700",
  Confirmed: "bg-emerald-100 text-emerald-700", Closed: "bg-gray-200 text-gray-600",
};

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "bookings", label: "Bookings", icon: CalendarCheck },
  { id: "inquiries", label: "Inquiries", icon: MessageSquare },
  { id: "gallery", label: "Gallery", icon: ImageIcon },
  { id: "testimonials", label: "Testimonials", icon: Star },
  { id: "content", label: "Site Content", icon: Settings },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const qc = useQueryClient();

  const stats = useQuery({ queryKey: ["stats"], queryFn: async () => (await api.get("/admin/stats")).data });
  const bookings = useQuery({ queryKey: ["ab"], queryFn: async () => (await api.get("/admin/bookings")).data });
  const inquiries = useQuery({ queryKey: ["ai"], queryFn: async () => (await api.get("/admin/inquiries")).data });
  const gallery = useQuery({ queryKey: ["gallery"], queryFn: async () => (await api.get("/gallery")).data });
  const testimonials = useQuery({ queryKey: ["testimonials"], queryFn: async () => (await api.get("/testimonials")).data });

  const invalidate = (...keys) => keys.forEach((k) => qc.invalidateQueries({ queryKey: [k] }));

  const setBookingStatus = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/admin/bookings/${id}`, { status }),
    onSuccess: () => { invalidate("ab", "stats"); toast.success("Status updated"); },
  });

  const doLogout = () => { logout(); nav("/admin/login"); };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="w-16 sm:w-60 bg-brand-navy text-white flex flex-col shrink-0">
        <div className="h-20 flex items-center px-4 sm:px-6 border-b border-white/10">
          <span className="font-serif text-lg hidden sm:block">WEDDING EVENTS</span>
          <span className="font-serif text-lg sm:hidden">WE</span>
        </div>
        <nav className="flex-1 py-4">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`w-full flex items-center gap-3 px-4 sm:px-6 py-3 text-sm transition-colors ${tab === t.id ? "bg-white/10 text-brand-gold border-l-2 border-brand-gold" : "text-white/70 hover:bg-white/5"}`}>
              <t.icon size={18} /> <span className="hidden sm:block">{t.label}</span>
            </button>
          ))}
        </nav>
        <button onClick={doLogout} data-testid="admin-logout" className="flex items-center gap-3 px-4 sm:px-6 py-4 text-sm text-white/70 hover:text-white border-t border-white/10">
          <LogOut size={18} /> <span className="hidden sm:block">Logout</span>
        </button>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-x-hidden">
        <header className="h-20 bg-white border-b flex items-center justify-between px-6">
          <h1 className="font-serif text-2xl text-brand-navy capitalize">{tab}</h1>
          <span className="text-sm text-brand-ink/60">{user?.email}</span>
        </header>

        <div className="p-6">
          {tab === "overview" && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: "Total Bookings", value: stats.data?.bookings, sub: `${stats.data?.new_bookings || 0} new` },
                { label: "Inquiries", value: stats.data?.inquiries },
                { label: "Gallery Items", value: stats.data?.gallery },
                { label: "Testimonials", value: stats.data?.testimonials },
              ].map((c) => (
                <div key={c.label} className="bg-white border rounded-sm p-6">
                  <p className="text-xs uppercase tracking-widest text-brand-ink/50">{c.label}</p>
                  <p className="font-serif text-4xl text-brand-navy mt-3">{c.value ?? "—"}</p>
                  {c.sub && <p className="text-xs text-brand-gold mt-1">{c.sub}</p>}
                </div>
              ))}
            </div>
          )}

          {tab === "bookings" && (
            <div className="bg-white border rounded-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-left text-xs uppercase tracking-wider text-brand-ink/60">
                  <tr><th className="p-3">Name</th><th className="p-3">Event</th><th className="p-3">Date</th><th className="p-3">Contact</th><th className="p-3">Services</th><th className="p-3">Status</th></tr>
                </thead>
                <tbody>
                  {(bookings.data || []).map((b) => (
                    <tr key={b.id} data-testid={`booking-row-${b.id}`} className="border-t hover:bg-slate-50 align-top">
                      <td className="p-3 font-medium">{b.name}<div className="text-xs text-brand-ink/50">{b.budget_range}</div></td>
                      <td className="p-3">{b.event_type}<div className="text-xs text-brand-ink/50">{b.guest_count && `${b.guest_count} guests`}</div></td>
                      <td className="p-3">{b.event_date || "—"}</td>
                      <td className="p-3 text-xs">{b.email}<div>{b.phone}</div></td>
                      <td className="p-3 text-xs max-w-[160px]">{(b.services || []).join(", ") || "—"}{b.message && <div className="text-brand-ink/50 mt-1 italic">“{b.message}”</div>}</td>
                      <td className="p-3">
                        <select value={b.status} data-testid={`booking-status-${b.id}`} onChange={(e) => setBookingStatus.mutate({ id: b.id, status: e.target.value })}
                          className={`text-xs px-2 py-1 rounded-sm border-0 font-medium ${statusColor[b.status] || ""}`}>
                          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                  {(bookings.data || []).length === 0 && <tr><td colSpan={6} className="p-8 text-center text-brand-ink/50">No bookings yet.</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {tab === "inquiries" && (
            <div className="grid md:grid-cols-2 gap-4">
              {(inquiries.data || []).map((q) => (
                <div key={q.id} data-testid={`inquiry-${q.id}`} className="bg-white border rounded-sm p-5">
                  <div className="flex justify-between"><p className="font-medium text-brand-navy">{q.name}</p><span className="text-xs text-brand-ink/40">{(q.created_at || "").slice(0, 10)}</span></div>
                  <p className="text-xs text-brand-ink/60">{q.email} · {q.phone || "—"}</p>
                  {q.subject && <p className="text-sm mt-2 font-medium">{q.subject}</p>}
                  <p className="text-sm text-brand-ink/70 mt-1">{q.message}</p>
                </div>
              ))}
              {(inquiries.data || []).length === 0 && <p className="text-brand-ink/50">No inquiries yet.</p>}
            </div>
          )}

          {tab === "gallery" && <GalleryManager items={gallery.data || []} invalidate={invalidate} />}
          {tab === "testimonials" && <TestimonialManager items={testimonials.data || []} invalidate={invalidate} />}
          {tab === "content" && <ContentManager />}
        </div>
      </main>
    </div>
  );
}

function GalleryManager({ items, invalidate }) {
  const [form, setForm] = useState({ title: "", category: "Weddings", url: "", type: "photo" });
  const [uploading, setUploading] = useState(false);
  const add = useMutation({
    mutationFn: () => api.post("/admin/gallery", { ...form }),
    onSuccess: () => { invalidate("gallery", "stats"); setForm({ title: "", category: "Weddings", url: "", type: "photo" }); toast.success("Added"); },
    onError: () => toast.error("Failed to add"),
  });
  const del = useMutation({ mutationFn: (id) => api.delete(`/admin/gallery/${id}`), onSuccess: () => { invalidate("gallery", "stats"); toast.success("Deleted"); } });

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, url: data.url, type: data.type, title: s.title || f.name.replace(/\.[^.]+$/, "") }));
      toast.success("File uploaded — now click Add to Gallery");
    } catch (err) {
      toast.error("Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <div>
      <div className="bg-white border rounded-sm p-5 mb-6 space-y-3">
        <div className="grid sm:grid-cols-3 gap-3">
          <div><label className="text-xs text-brand-ink/60">Title</label><Input data-testid="gallery-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
          <div><label className="text-xs text-brand-ink/60">Category</label><Input data-testid="gallery-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
          <div>
            <label className="text-xs text-brand-ink/60">Type</label>
            <select data-testid="gallery-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full h-10 border rounded-sm px-2 text-sm">
              <option value="photo">Photo</option>
              <option value="video">Video</option>
            </select>
          </div>
        </div>
        <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
          <div><label className="text-xs text-brand-ink/60">Media URL (or upload a file →)</label><Input data-testid="gallery-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} /></div>
          <label className="inline-flex justify-center items-center gap-2 border border-brand-navy/30 text-brand-navy py-2.5 px-4 rounded-sm cursor-pointer hover:bg-slate-50 whitespace-nowrap">
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} {uploading ? "Uploading…" : "Upload File"}
            <input type="file" accept="image/*,video/*" data-testid="gallery-upload-input" className="hidden" onChange={handleUpload} disabled={uploading} />
          </label>
        </div>
        <button data-testid="gallery-add-btn" disabled={!form.title || !form.url || uploading} onClick={() => add.mutate()} className="inline-flex justify-center items-center gap-2 bg-brand-navy text-white py-2.5 px-6 rounded-sm disabled:opacity-50"><Plus size={16} /> Add to Gallery</button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {items.map((g) => (
          <div key={g.id} className="relative group border rounded-sm overflow-hidden bg-white">
            <img src={g.url} alt={g.title} className="w-full h-36 object-cover" />
            <div className="p-2"><p className="text-xs font-medium truncate">{g.title}</p><p className="text-[10px] text-brand-ink/50">{g.category}</p></div>
            <button data-testid={`gallery-del-${g.id}`} onClick={() => del.mutate(g.id)} className="absolute top-2 right-2 bg-white/90 text-red-600 p-1.5 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ContentManager() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["site-content"], queryFn: async () => (await api.get("/site-content")).data });
  const [form, setForm] = useState(null);
  useEffect(() => { if (data) setForm((prev) => prev || { ...data }); }, [data]);
  const save = useMutation({
    mutationFn: () => api.put("/admin/site-content", form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["site-content"] }); toast.success("Content saved"); },
    onError: () => toast.error("Save failed"),
  });
  if (!form) return <p className="text-brand-ink/50">Loading…</p>;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="max-w-3xl space-y-6" data-testid="content-manager">
      <div className="bg-white border rounded-sm p-6">
        <h3 className="font-serif text-xl text-brand-navy mb-4">Contact Details</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="text-xs text-brand-ink/60">Phone 1</label><Input data-testid="content-phone" value={form.phone || ""} onChange={set("phone")} /></div>
          <div><label className="text-xs text-brand-ink/60">Phone 2</label><Input data-testid="content-phone2" value={form.phone2 || ""} onChange={set("phone2")} /></div>
          <div><label className="text-xs text-brand-ink/60">WhatsApp number (digits only)</label><Input data-testid="content-whatsapp" value={form.whatsapp || ""} onChange={set("whatsapp")} /></div>
          <div><label className="text-xs text-brand-ink/60">Email</label><Input data-testid="content-email" value={form.email || ""} onChange={set("email")} /></div>
          <div className="sm:col-span-2"><label className="text-xs text-brand-ink/60">Address</label><Input data-testid="content-address" value={form.address || ""} onChange={set("address")} /></div>
        </div>
      </div>
      <div className="bg-white border rounded-sm p-6">
        <h3 className="font-serif text-xl text-brand-navy mb-4">Home Page</h3>
        <div className="space-y-4">
          <div><label className="text-xs text-brand-ink/60">Hero subtitle</label><Textarea rows={3} data-testid="content-hero" value={form.hero_subtitle || ""} onChange={set("hero_subtitle")} placeholder="Leave blank to use the default text" /></div>
          <div><label className="text-xs text-brand-ink/60">About-section heading</label><Input data-testid="content-abouttitle" value={form.home_about_title || ""} onChange={set("home_about_title")} /></div>
          <div><label className="text-xs text-brand-ink/60">About-section text</label><Textarea rows={3} data-testid="content-aboutsub" value={form.home_about_subtitle || ""} onChange={set("home_about_subtitle")} /></div>
        </div>
      </div>
      <div className="bg-white border rounded-sm p-6">
        <h3 className="font-serif text-xl text-brand-navy mb-4">About Page Story</h3>
        <label className="text-xs text-brand-ink/60">One paragraph per line</label>
        <Textarea rows={6} data-testid="content-aboutstory" value={form.about_story || ""} onChange={set("about_story")} />
      </div>
      <button data-testid="content-save-btn" onClick={() => save.mutate()} disabled={save.isPending} className="inline-flex items-center gap-2 bg-brand-navy text-white px-6 py-2.5 rounded-sm disabled:opacity-50">
        {save.isPending && <Loader2 size={16} className="animate-spin" />} Save Changes
      </button>
    </div>
  );
}

function TestimonialManager({ items, invalidate }) {
  const [form, setForm] = useState({ name: "", event_type: "", rating: 5, quote: "" });
  const add = useMutation({
    mutationFn: () => api.post("/admin/testimonials", { ...form, rating: Number(form.rating) }),
    onSuccess: () => { invalidate("testimonials", "stats"); setForm({ name: "", event_type: "", rating: 5, quote: "" }); toast.success("Added"); },
    onError: () => toast.error("Failed to add"),
  });
  const del = useMutation({ mutationFn: (id) => api.delete(`/admin/testimonials/${id}`), onSuccess: () => { invalidate("testimonials", "stats"); toast.success("Deleted"); } });

  return (
    <div>
      <div className="bg-white border rounded-sm p-5 mb-6 space-y-3">
        <div className="grid sm:grid-cols-3 gap-3">
          <div><label className="text-xs text-brand-ink/60">Name</label><Input data-testid="testi-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><label className="text-xs text-brand-ink/60">Event Type</label><Input data-testid="testi-event" value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} /></div>
          <div><label className="text-xs text-brand-ink/60">Rating (1-5)</label><Input type="number" min={1} max={5} data-testid="testi-rating" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} /></div>
        </div>
        <div><label className="text-xs text-brand-ink/60">Quote</label><Textarea rows={2} data-testid="testi-quote" value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} /></div>
        <button data-testid="testi-add-btn" disabled={!form.name || !form.quote} onClick={() => add.mutate()} className="inline-flex items-center gap-2 bg-brand-navy text-white px-5 py-2.5 rounded-sm disabled:opacity-50"><Plus size={16} /> Add Testimonial</button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {items.map((t) => (
          <div key={t.id} className="relative border rounded-sm p-5 bg-white">
            <button data-testid={`testi-del-${t.id}`} onClick={() => del.mutate(t.id)} className="absolute top-3 right-3 text-red-600"><Trash2 size={16} /></button>
            <p className="font-medium text-brand-navy">{t.name}</p>
            <p className="text-xs text-brand-gold uppercase tracking-widest">{t.event_type} · {t.rating}★</p>
            <p className="text-sm text-brand-ink/70 mt-2 italic">“{t.quote}”</p>
          </div>
        ))}
      </div>
    </div>
  );
}
